import { ReactElement, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { AxiosError } from 'axios'
import { find, invert, every, isNull } from 'lodash-es'
import { Typography, Stack, Button } from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { SaveButton } from '@ui/actions'
import { Form, Autocomplete, ErrorFieldBase, Label } from '@ui/forms'
import { useSnackbar } from '@ui/snackbar'
import { Box } from '@ui/structure'
import { useSystemManagerStore } from '@editorStores'
import { useProjectEarthMassForce, useResultsInvalidation } from '@editorHooks'
import { getProjectAssemblies, getModel } from '@queries'
import { editProjectAssembly, createProjectAssembly, updateElementAssemblies } from '@mutations'
import { CreateDialog, EditAssemblyDialog, assemblySchema } from 'src/components/manager/assemblies'
import { usageToElementType } from 'src/constants/elements'
import { buildErrorMessage } from 'src/constants/errors'
import { getDefaultShortName } from 'src/utils/assemblies'

interface Props {
  selectedElements: string[]
  elementType: ElementTypes
  onClose?: () => void
  showSelectedElementsNumber?: boolean
  autoFocus?: boolean
  showBorder?: boolean
  /** Called after the assignment or an assembly was saved successfully. */
  onSaved?: () => void
}

const AssemblyAssignmentForm = ({
  onClose,
  selectedElements,
  elementType,
  showSelectedElementsNumber = true,
  autoFocus = true,
  onSaved,
}: Props): ReactElement => {
  const earthMassForce = useProjectEarthMassForce()
  const { projectId } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()
  const [createValues, setCreateValues] = useState<Assembly | null>(null)
  const [editValues, setEditValues] = useState<Assembly | null>(null)

  const invalidateResults = useResultsInvalidation()

  const assemblies = useSystemManagerStore(state => state.assemblies)
  const assemblyAssignment = useSystemManagerStore(state => state.assemblyAssignment)
  const assemblyOptions = useMemo(() => {
    return assemblies.reduce<
      {
        value: string
        label: string
      }[]
    >((acc, assembly) => {
      const type = usageToElementType[assembly.usage_type]
      // only show assemblies for currently selected element type
      if (type !== elementType) return acc

      return [
        ...acc,
        {
          value: assembly.guid,
          label: assembly.name,
        },
      ]
    }, [])
  }, [assemblies, elementType])

  const selectedAssemblyGuid = useMemo(() => {
    const assemblyGuids = selectedElements.map(
      elementGuid => find(assemblyAssignment, ['element_guid', elementGuid])?.assembly_guid,
    )
    const isSameAssembly = every(assemblyGuids, (value, i, array) => value === array[0])

    return isSameAssembly ? assemblyGuids[0] : null
  }, [assemblyAssignment, selectedElements])

  const handleSubmit = async ({ selectedAssemblyGuid }: { selectedAssemblyGuid: string }) => {
    await mutateAsync({
      element_guids: selectedElements,
      assembly_guid: selectedAssemblyGuid,
    })
  }

  const { mutateAsync, isLoading } = useMutation(
    (data: { element_guids: string[]; assembly_guid: string }) =>
      updateElementAssemblies.request(projectId as string, data),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(getModel.getKey(projectId))
        invalidateResults(projectId as string)

        enqueueSnackbar('Aufbauzuweisung erfolgreich gespeichert', { variant: 'success' })
        onSaved?.()
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Speichern der Aufbauzuweisung'), {
          variant: 'error',
        })
      },
    },
  )

  const createMutation = useMutation(
    (data: Assembly) => createProjectAssembly.request(projectId as string, data),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(getProjectAssemblies.getKey(projectId))
        await queryClient.invalidateQueries(getModel.getKey(projectId))
        invalidateResults(projectId as string)

        enqueueSnackbar('Aufbau erfolgreich angelegt', { variant: 'success' })
        onSaved?.()
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Speichern des Aufbaus'), {
          variant: 'error',
        })
      },
    },
  )

  const editMutation = useMutation(
    (data: Assembly) => editProjectAssembly.request(projectId as string, data),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(getProjectAssemblies.getKey(projectId))
        await queryClient.invalidateQueries(getModel.getKey(projectId))
        invalidateResults(projectId as string)

        enqueueSnackbar('Aufbau erfolgreich gespeichert', { variant: 'success' })
        onSaved?.()
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Speichern des Aufbaus'), {
          variant: 'error',
        })
      },
    },
  )

  return (
    <>
      <Form
        onSubmit={handleSubmit}
        defaultValues={{ selectedAssemblyGuid }}
        enableReinitialize
        data-cy={'form-' + elementType + '-assembly-assignment'}
      >
        {({ formState: { isDirty } }) => (
          <>
            {showSelectedElementsNumber ? (
              <>
                <Typography variant="h6" sx={{ textAlign: 'center' }}>
                  Ausgewählt: {selectedElements.length}
                </Typography>
                <Typography mb={1}>
                  Weisen Sie den ausgewählten Elementen einen Aufbau zu
                </Typography>
              </>
            ) : (
              <Label>Aufbau</Label>
            )}
            <Autocomplete
              name="selectedAssemblyGuid"
              options={assemblyOptions}
              onEdit={({ value }) => setEditValues(find(assemblies, ['guid', value]) || null)}
              onAdd={() => {
                const usageType = invert(usageToElementType)[elementType] as AssemblyTypes
                setCreateValues({
                  ...assemblySchema.getDefault(),
                  kind: assemblySchema.getDefault().kind as AssemblyKind,
                  usage_type: usageType,
                  isStandard: false,
                  short_name: getDefaultShortName(usageType, assemblies),
                })
              }}
              onAddLabel="Neuen Aufbau anlegen"
              dataCy="select-assembly-autocomplete"
              autoFocus={autoFocus}
            />
            {isNull(selectedAssemblyGuid) && (
              <Box mt={1}>
                <ErrorFieldBase
                  severity="warning"
                  message="Elemente haben unterschiedliche Aufbauten"
                />
              </Box>
            )}
            <Stack direction="row" justifyContent="end" spacing={1} mt={1}>
              <SaveButton
                loading={isLoading}
                disabled={!isDirty}
                size="small"
                fullWidth
                type="submit"
                data-cy="submit-assembly-assignment"
              >
                Speichern
              </SaveButton>
              {onClose && (
                <Button
                  onClick={onClose}
                  size="small"
                  variant="outlined"
                  fullWidth
                  data-cy="btn-unselect-assembly-assignment"
                >
                  Auswahl aufheben
                </Button>
              )}
            </Stack>
          </>
        )}
      </Form>
      {createValues && (
        <CreateDialog
          mutation={createMutation}
          open
          onClose={() => setCreateValues(null)}
          assembly={createValues}
          earthMassForce={earthMassForce}
        />
      )}
      {editValues && (
        <EditAssemblyDialog
          mutation={editMutation}
          open
          onClose={() => setEditValues(null)}
          assembly={editValues}
          earthMassForce={earthMassForce}
        />
      )}
    </>
  )
}

export default AssemblyAssignmentForm
